import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, XCircle, FileText } from 'lucide-react';
import clsx from 'clsx';
import { ImportFileResult } from '../../utils/playbookImportUtils';

interface ImportResultsDialogProps {
  isOpen: boolean;
  results: ImportFileResult[];
  onConfirm: (selectedResults: ImportFileResult[]) => void;
  onCancel: () => void;
}

const ImportResultsDialog: React.FC<ImportResultsDialogProps> = ({
  isOpen,
  results,
  onConfirm,
  onCancel
}) => {
    const validResults = results.filter(r => r.validationResult.isValid);
  const invalidResults = results.filter(r => !r.validationResult.isValid);
  const warningResults = results.filter(r => r.validationResult.isValid && r.validationResult.warningMessage);

  const handleConfirm = () => {
    onConfirm(validResults); // Confirm all valid results since there's no selection
  };

  const getStatusIcon = (result: ImportFileResult) => {
    if (!result.validationResult.isValid) {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    if (result.validationResult.warningMessage) {
      return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  };

  const getStatusText = (result: ImportFileResult) => {
    if (!result.validationResult.isValid) {
      return 'Error';
    }
    if (result.validationResult.warningMessage) {
      return 'Warning';
    }
    return 'Valid';
  };

  const getStatusColor = (result: ImportFileResult) => {
    if (!result.validationResult.isValid) {
      return 'text-red-600 dark:text-red-400';
    }
    if (result.validationResult.warningMessage) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    return 'text-green-600 dark:text-green-400';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-100 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative max-w-2xl w-full mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Import Results
                </h2>
              </div>
              <button
                onClick={onCancel}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {/* Summary */}
            <div className="mb-6 p-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                    {validResults.length}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Valid</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                    {warningResults.length}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Warnings</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                    {invalidResults.length}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Errors</div>
                </div>
              </div>
            </div>

            {/* File Results */}
            <div className="space-y-3">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={clsx(
                    "border rounded-lg p-4 transition-colors",
                    result.validationResult.isValid
                      ? "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                      : "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
                  )}
                >
                  <div className="flex items-start space-x-3">
                    {/* File icon */}
                    <div className="flex-shrink-0 pt-1">
                      <FileText className="w-5 h-5 text-gray-400" />
                    </div>

                    {/* File details */}
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center justify-between space-x-2 mb-1"> {/* Added justify-between here */}
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {result.fileName}.{result.fileType === 'playbook' ? 'yaml' : 'yaml'}
                          </h4>
                          <span className={clsx(
                            "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                            result.fileType === 'playbook' 
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                          )}>
                            {result.fileType}
                          </span>
                        </div>
                        {/* Status - Moved to far right */}
                        <div className="flex items-center space-x-2 flex-shrink-0"> {/* Added flex-shrink-0 */}
                          {getStatusIcon(result)}
                          <span className={clsx("text-sm font-medium", getStatusColor(result))}>
                            {getStatusText(result)}
                          </span>
                        </div>
                      </div>

                      {/* Messages */}
                      {result.validationResult.errorMessage && (
                        <div className="text-sm text-red-600 dark:text-red-400 mb-2">
                          {result.validationResult.errorMessage}
                        </div>
                      )}
                      {result.validationResult.warningMessage && (
                        <div className="text-sm text-yellow-600 dark:text-yellow-400 mb-2">
                          {result.validationResult.warningMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Instructions */}
            {validResults.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Import Instructions
                </h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Playbook files will update your transform configurations</li>
                  <li>• Connection files will update your connection settings</li>
                  <li>• Default transforms will be converted to event-specific transforms</li>
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={validResults.length === 0} // Disable if no valid results to import
              className={clsx(
                "px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors",
                validResults.length > 0
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              )}
            >
              Import Valid ({validResults.length}) {/* Updated button text */}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImportResultsDialog;
